import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Icons,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import {
  getBackButtonRoute,
  goToMySpaceNav,
} from '@core/constants/backButtonNavigation';
import pushRoute from '@app/utils/routing';
import { fetchImportantPosts } from '@core/actions/classRoom/importantPosts';
import { importantPostsSelector } from '@core/selectors/classroom';
import type { AulaState } from '@core/types/state';
import commonRoomIcon from '@static/common_room.svg';
import { createStyles } from './CommonRoomResponsive.styles';
import CommonRoomInfoAlert, {
  CommonRoomInfoButton,
  CommonRoomInfoModal,
} from './CommonRoomInfoAlert';
import ParticipantsPreview from './ParticipantsPreview/ParticipantsPreview';
import type { ParticipantsPreviewParticipant } from './ParticipantsPreview/ParticipantsPreview.types';
import SidebarPinnedPosts from './Sidebar/PinnedPosts/PinnedPosts';

interface CommonRoomSidebarProps {
  participantTotal: number;
  participants: ParticipantsPreviewParticipant[];
  spaceId?: string;
}

const CommonRoomSidebar = ({
  participantTotal,
  participants,
  spaceId,
}: CommonRoomSidebarProps) => {
  const dispatch = useDispatch();
  const pinnedPosts = useSelector(importantPostsSelector);
  const isSpaceLoaded = useSelector(
    (state: AulaState) => !!spaceId && !!state.data.classRooms[spaceId]
  );

  useEffect(() => {
    if (spaceId && isSpaceLoaded) {
      dispatch(
        fetchImportantPosts({
          spaceId,
          after: undefined,
          tagId: undefined,
        })
      );
    }
  }, [dispatch, isSpaceLoaded, spaceId]);

  const theme = useTheme();
  const styles = createStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const backRoute =
    getBackButtonRoute() || (spaceId && goToMySpaceNav(spaceId));

  const handleBack = () => {
    if (backRoute) pushRoute(backRoute);
  };

  return (
    <Box component="aside" sx={styles.sidebar} aria-label="Common Room">
      <Box sx={styles.sidebarHeader}>
        <Button
          kind="secondary"
          onClick={handleBack}
          disabled={!backRoute}
          startIcon={<Icons.WestOutlined aria-hidden="true" />}
          aria-label="Back to space navigation"
          sx={styles.backButton}
        >
          Back
        </Button>

        <Box sx={styles.titleRow}>
          <Box sx={styles.titleIcon} aria-hidden="true">
            <img
              src={commonRoomIcon}
              alt=""
              width="40"
              height="40"
              data-testid="common-room-icon"
            />
          </Box>
          <Typography component="h1" variant="h5" sx={styles.title}>
            Common room
          </Typography>
          {(!isDesktop || !isAlertVisible) && (
            <Box sx={{ ml: 'auto' }}>
              <CommonRoomInfoButton
                isDesktop={isDesktop}
                onClick={() => {
                  if (!isDesktop) setIsInfoModalOpen(true);
                }}
              />
            </Box>
          )}
        </Box>

        <ParticipantsPreview
          participantTotal={participantTotal}
          participants={participants}
        />
        {isDesktop && (
          <CommonRoomInfoAlert
            visible={isAlertVisible}
            isDesktop={isDesktop}
            onClose={() => setIsAlertVisible(false)}
          />
        )}
        {!isDesktop && (
          <CommonRoomInfoModal
            open={isInfoModalOpen}
            onClose={() => setIsInfoModalOpen(false)}
          />
        )}
      </Box>
      {isMdUp && <SidebarPinnedPosts pinnedPosts={pinnedPosts} />}
    </Box>
  );
};

export default CommonRoomSidebar;
